// Date utility helpers (client-side, local timezone)

export function toISODate(d) {
  if (!d) return "";
  const date = d instanceof Date ? d : new Date(d);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function todayISO() {
  return toISODate(new Date());
}

export function parseISODate(s) {
  // Parse "YYYY-MM-DD" as a local date (no TZ offset issues)
  if (!s) return null;
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
}

export function formatFriendlyDate(s) {
  const d = parseISODate(s);
  if (!d) return s;
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function formatShortDate(s) {
  const d = parseISODate(s);
  if (!d) return s;
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

// Shift an ISO date string by N days (can be negative)
export function shiftISO(iso, days) {
  const d = parseISODate(iso);
  if (!d) return iso;
  d.setDate(d.getDate() + days);
  return toISODate(d);
}

// Shift an ISO date string by N months (approximate - same day-of-month)
export function shiftMonthISO(iso, months) {
  const d = parseISODate(iso);
  if (!d) return iso;
  d.setMonth(d.getMonth() + months);
  return toISODate(d);
}

// Human-friendly label for the current period range
export function periodLabel(period, refISO) {
  const d = parseISODate(refISO);
  if (!d) return "";
  if (period === "day") {
    return d.toLocaleDateString(undefined, {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  }
  if (period === "week") {
    const weekday = d.getDay() === 0 ? 6 : d.getDay() - 1; // Monday = 0
    const start = new Date(d);
    start.setDate(d.getDate() - weekday);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    const fmt = (x) =>
      x.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    const endFmt = `${fmt(end)}, ${end.getFullYear()}`;
    return `${fmt(start)} – ${endFmt}`;
  }
  if (period === "month") {
    return d.toLocaleDateString(undefined, {
      month: "long",
      year: "numeric",
    });
  }
  return "";
}

export function formatHours(h) {
  const n = Number(h || 0);
  if (!Number.isFinite(n)) return "0h";
  const whole = Math.floor(n);
  const minutes = Math.round((n - whole) * 60);
  if (minutes === 0) return `${whole}h`;
  if (whole === 0) return `${minutes}m`;
  return `${whole}h ${minutes}m`;
}

// Compact relative time for "last seen" / "last login" columns. Falls back to
// a friendly absolute date for anything older than a week so admins can scan
// week-of-time without doing math in their head.
export function formatRelativeTime(input) {
  if (!input) return null;
  const t = new Date(input).getTime();
  if (!Number.isFinite(t)) return null;
  const seconds = Math.floor((Date.now() - t) / 1000);
  if (seconds < 0) return "just now";
  if (seconds < 45) return "just now";
  if (seconds < 90) return "1m ago";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  const d = new Date(t);
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: d.getFullYear() === new Date().getFullYear() ? undefined : "numeric",
  });
}

export function formatElapsed(startedAt) {
  if (!startedAt) return "00:00:00";
  const ms = Date.now() - new Date(startedAt).getTime();
  const s = Math.max(Math.floor(ms / 1000), 0);
  const hh = String(Math.floor(s / 3600)).padStart(2, "0");
  const mm = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
}
